(() => {
  'use strict';

  const dialog = document.getElementById('os-favorites');
  if (!dialog || dialog.dataset.initialized === 'true') return;
  const list = dialog.querySelector('[data-os-favorites-list]');
  const empty = dialog.querySelector('[data-os-favorites-empty]');
  const message = dialog.querySelector('[data-os-favorites-message]');
  const status = document.getElementById('os-favorites-status');
  if (!list || !empty || !message) return;
  dialog.dataset.initialized = 'true';

  const KEY = 'oneshoes:favorites:v1';
  const MAX = 100;
  const copy = dialog.dataset;
  const root = new URL(copy.rootUrl || '/', window.location.origin);
  if (root.origin !== window.location.origin) return;
  if (!root.pathname.endsWith('/')) root.pathname += '/';
  const pending = new Map();
  const productCache = new Map();
  let storageProblem = false;
  let generation = 0;
  let controller = null;
  let opener = null;
  let shouldRestoreFocus = true;
  let syncing = false;
  let observedSyncQueued = false;
  let announcementTimer = null;
  let announcementClearTimer = null;
  let favorites = readStorage() || [];

  function validHandle(value) {
    return typeof value === 'string' && value.length > 0 && value.length <= 255 &&
      value !== '.' && value !== '..' && !/[\s/\\?#\u0000-\u001f\u007f]/u.test(value);
  }

  function parseStorage(raw) {
    if (!raw) return [];
    try {
      const value = JSON.parse(raw);
      return Array.isArray(value) ? [...new Set(value.filter(validHandle))].slice(0, MAX) : [];
    } catch (_) {
      return [];
    }
  }

  function readStorage() {
    try {
      const saved = parseStorage(window.localStorage.getItem(KEY));
      if (!pending.size) storageProblem = false;
      return saved;
    } catch (_) {
      storageProblem = true;
      return null;
    }
  }

  function applyPending(values) {
    const next = new Set(values);
    pending.forEach((saved, handle) => {
      if (saved) next.add(handle);
      else next.delete(handle);
    });
    return [...next].slice(0, MAX);
  }

  function text(key, fallback = '') {
    return copy[key] || fallback;
  }

  function announce(value) {
    if (!status) return;
    window.clearTimeout(announcementTimer);
    window.clearTimeout(announcementClearTimer);
    status.textContent = '';
    announcementTimer = window.setTimeout(() => {
      status.textContent = value;
      announcementClearTimer = window.setTimeout(() => { status.textContent = ''; }, 4000);
    }, 30);
  }

  function updateStorageNotice() {
    message.textContent = storageProblem ? text('storageNotice') : '';
    message.hidden = !storageProblem;
  }

  function syncButtons() {
    syncing = true;
    const saved = new Set(favorites);
    document.querySelectorAll('[data-os-favorite]').forEach((button) => {
      const pressed = saved.has(button.value);
      button.hidden = false;
      button.setAttribute('aria-pressed', String(pressed));
      const label = pressed ? button.dataset.labelRemove : button.dataset.labelAdd;
      if (label) button.setAttribute('aria-label', label);
    });
    document.querySelectorAll('[data-os-favorites-open]').forEach((button) => {
      button.hidden = false;
      button.setAttribute('aria-expanded', String(dialog.open));
    });
    document.querySelectorAll('[data-os-favorites-count]').forEach((count) => {
      const value = String(favorites.length);
      if (count.textContent !== value) count.textContent = value;
      count.hidden = favorites.length === 0;
    });
    empty.hidden = favorites.length !== 0;
    updateStorageNotice();
    syncing = false;
  }

  function productUrl(handle, json = false) {
    return new URL(`products/${encodeURIComponent(handle)}${json ? '.js' : ''}`, root).href;
  }

  function knownTitle(handle) {
    for (const button of document.querySelectorAll('[data-os-favorite]')) {
      if (button.value === handle && button.dataset.productTitle) return button.dataset.productTitle;
    }
    return handle.replace(/[-_]+/g, ' ');
  }

  function saveChange(handle, saved, focusIndex) {
    if (!validHandle(handle)) return;
    const latest = readStorage();
    const current = applyPending(latest === null ? favorites : latest);
    if (saved && !current.includes(handle) && current.length >= MAX) {
      favorites = current;
      syncButtons();
      announce(text('limit'));
      return;
    }
    pending.set(handle, saved);
    favorites = applyPending(current);
    try {
      window.localStorage.setItem(KEY, JSON.stringify(favorites));
      pending.clear();
      storageProblem = false;
    } catch (_) {
      storageProblem = true;
    }
    syncButtons();
    announce([text(saved ? 'added' : 'removed'), storageProblem ? text('storageNotice') : ''].filter(Boolean).join(' '));
    if (dialog.open) renderFavorites(focusIndex);
  }

  function make(tag, className, value) {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (value !== undefined) element.textContent = value;
    return element;
  }

  function createRow(handle) {
    const title = knownTitle(handle);
    const row = make('li', 'os-favorites-item');
    row.dataset.productHandle = handle;
    const media = make('div', 'os-favorites-image');
    media.setAttribute('aria-hidden', 'true');
    const info = make('div', 'os-favorites-info');
    const name = make('h3', 'os-favorites-name', title);
    const price = make('p', 'os-favorites-price');
    price.hidden = true;
    const link = make('a', 'os-favorites-product-link', text('viewProduct'));
    link.href = productUrl(handle);
    link.hidden = true;
    const state = make('p', 'os-favorites-state', text('loading'));
    const remove = make('button', 'os-favorites-remove', text('remove'));
    remove.type = 'button';
    remove.dataset.osFavoritesRemove = handle;
    remove.setAttribute('aria-label', `${text('remove')}: ${title}`);
    info.append(name, price, link, state);
    row.append(media, info, remove);
    return { row, media, info, name, price, link, state, remove, handle };
  }

  function formatPrice(product) {
    const cents = typeof product.price === 'number' ? product.price : NaN;
    if (!Number.isFinite(cents) || cents < 0) return '';
    let formatted;
    try {
      formatted = new Intl.NumberFormat(copy.locale || document.documentElement.lang || 'ro-RO', {
        style: 'currency', currency: copy.currency || 'RON',
      }).format(cents / 100);
    } catch (_) {
      formatted = `${(cents / 100).toFixed(2)} ${copy.currency || 'RON'}`;
    }
    return product.price_varies ? text('priceFrom', '{price}').replace('{price}', formatted) : formatted;
  }

  function fillRow(parts, result) {
    if (result.kind !== 'product') {
      parts.state.textContent = text(result.kind === 'unavailable' ? 'unavailable' : 'loadError');
      if (result.kind === 'error') {
        const retry = make('button', 'os-favorites-retry', text('retry'));
        retry.type = 'button';
        retry.dataset.osFavoritesRetry = parts.handle;
        parts.info.append(retry);
      }
      return;
    }
    const product = result.product;
    const title = typeof product.title === 'string' && product.title.trim() ? product.title : knownTitle(parts.handle);
    const nameLink = make('a', '', title);
    nameLink.href = productUrl(parts.handle);
    parts.name.replaceChildren(nameLink);
    parts.remove.setAttribute('aria-label', `${text('remove')}: ${title}`);
    parts.link.hidden = false;
    parts.price.textContent = formatPrice(product);
    parts.price.hidden = !parts.price.textContent;
    parts.state.textContent = product.available === false ? text('unavailable') : '';
    parts.state.hidden = product.available !== false;
    const imageValue = product.featured_image || (Array.isArray(product.images) ? product.images[0] : null);
    if (typeof imageValue !== 'string' || !imageValue.trim()) return;
    try {
      const imageUrl = new URL(imageValue.trim(), window.location.origin);
      if (!['http:', 'https:'].includes(imageUrl.protocol)) return;
      imageUrl.searchParams.set('width', '400');
      const image = make('img', 'os-favorites-image');
      image.width = 400;
      image.height = 400;
      image.loading = 'lazy';
      image.decoding = 'async';
      image.alt = title;
      image.addEventListener('error', () => image.replaceWith(parts.media), { once: true });
      image.src = imageUrl.href;
      parts.media.replaceWith(image);
    } catch (_) {
    }
  }

  async function loadProduct(handle, signal) {
    if (productCache.has(handle)) return productCache.get(handle);
    try {
      const response = await fetch(productUrl(handle, true), {
        credentials: 'same-origin', headers: { Accept: 'application/json' }, signal,
      });
      if (response.status === 404 || response.status === 410) return { kind: 'unavailable' };
      if (!response.ok) return { kind: 'error' };
      const product = await response.json();
      if (!product || typeof product !== 'object' || typeof product.title !== 'string') return { kind: 'error' };
      if (signal.aborted) return null;
      const result = { kind: 'product', product };
      productCache.set(handle, result);
      return result;
    } catch (_) {
      return signal.aborted ? null : { kind: 'error' };
    }
  }

  function stopRequests() {
    generation += 1;
    if (controller) controller.abort();
    controller = null;
    list.removeAttribute('aria-busy');
  }

  function renderFavorites(focusIndex) {
    stopRequests();
    if (!dialog.open) return;
    const activeGeneration = generation;
    controller = new AbortController();
    const signal = controller.signal;
    const rows = favorites.map(createRow);
    list.replaceChildren(...rows.map((parts) => parts.row));
    empty.hidden = rows.length !== 0;
    if (Number.isInteger(focusIndex)) {
      const button = rows[Math.min(focusIndex, rows.length - 1)]?.remove || dialog.querySelector('[data-os-favorites-close]');
      button?.focus({ preventScroll: true });
    }
    if (!rows.length) return;
    list.setAttribute('aria-busy', 'true');
    let cursor = 0;
    async function worker() {
      while (cursor < rows.length && !signal.aborted) {
        const parts = rows[cursor++];
        const result = await loadProduct(parts.handle, signal);
        if (!result || signal.aborted || activeGeneration !== generation || !dialog.open) return;
        fillRow(parts, result);
      }
    }
    Promise.all(Array.from({ length: Math.min(4, rows.length) }, worker)).then(() => {
      if (activeGeneration === generation) list.removeAttribute('aria-busy');
    });
  }

  function refreshFromStorage(render = true) {
    const latest = readStorage();
    if (latest !== null) favorites = applyPending(latest);
    syncButtons();
    if (render && dialog.open) renderFavorites();
  }

  function openFavorites(button) {
    if (dialog.open) return;
    opener = button;
    shouldRestoreFocus = true;
    refreshFromStorage(false);
    productCache.clear();
    try {
      dialog.showModal();
    } catch (_) {
      announce(text('loadError'));
      return;
    }
    document.documentElement.classList.add('os-favorites-open');
    document.querySelectorAll('[data-os-favorites-open]').forEach((trigger) => trigger.setAttribute('aria-expanded', 'true'));
    dialog.querySelector('[data-os-favorites-close]')?.focus({ preventScroll: true });
    renderFavorites();
  }

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest('[data-os-favorite], [data-os-favorites-open], [data-os-favorites-close], [data-os-favorites-remove], [data-os-favorites-retry]');
    if (!button || button.disabled) return;
    event.preventDefault();
    event.stopPropagation();
    if (button.hasAttribute('data-os-favorite')) {
      saveChange(button.value, !favorites.includes(button.value));
    } else if (button.hasAttribute('data-os-favorites-open')) {
      openFavorites(button);
    } else if (button.hasAttribute('data-os-favorites-close')) {
      dialog.close();
    } else if (button.hasAttribute('data-os-favorites-remove')) {
      const row = button.closest('.os-favorites-item');
      saveChange(button.dataset.osFavoritesRemove, false, Array.from(list.children).indexOf(row));
    } else {
      const handle = button.dataset.osFavoritesRetry;
      productCache.delete(handle);
      const row = button.closest('.os-favorites-item');
      renderFavorites(Array.from(list.children).indexOf(row));
    }
  }, true);

  dialog.addEventListener('click', (event) => {
    if (event.target !== dialog) return;
    const rect = dialog.getBoundingClientRect();
    if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dialog.close();
  });

  dialog.addEventListener('close', () => {
    if (dialog.open) return;
    stopRequests();
    document.documentElement.classList.remove('os-favorites-open');
    document.querySelectorAll('[data-os-favorites-open]').forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
    if (shouldRestoreFocus) {
      const target = opener?.isConnected ? opener : document.querySelector('[data-os-favorites-open]');
      target?.focus({ preventScroll: true });
    }
  });

  window.addEventListener('storage', (event) => {
    if (event.key === KEY || event.key === null) refreshFromStorage();
  });
  window.addEventListener('pageshow', () => refreshFromStorage());
  window.addEventListener('pagehide', () => {
    shouldRestoreFocus = false;
    stopRequests();
    document.documentElement.classList.remove('os-favorites-open');
    if (dialog.open) dialog.close();
  });

  new MutationObserver((records) => {
    if (syncing || observedSyncQueued || !records.some((record) => Array.from(record.addedNodes).some((node) =>
      node instanceof Element && (node.matches('[data-os-favorite], [data-os-favorites-count]') || node.querySelector('[data-os-favorite], [data-os-favorites-count]'))
    ))) return;
    observedSyncQueued = true;
    queueMicrotask(() => {
      observedSyncQueued = false;
      syncButtons();
    });
  }).observe(document.body, { childList: true, subtree: true });

  syncButtons();
})();
